import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Step1Address from "./OnboardingSteps/Step1Address";
import Step2Personal from "./OnboardingSteps/Step2Personal";
import Step3License from "./OnboardingSteps/Step3License";
import Step4Qualifications from "./OnboardingSteps/Step4Qualifications";
import Step5AdditionalLicenses from "./OnboardingSteps/Step5AdditionalLicenses";
import Step6DriverQuestions from "./OnboardingSteps/Step6DriverQuestions";
import Step7FinalQuestions from "./OnboardingSteps/Step7FinalQuestions";
import Step8WorkHistoryPage from "./OnboardingSteps/Step8WorkHistoryPage";
import Step9ReviewWorkHistoryPage from "./OnboardingSteps/Step9ReviewWorkHistoryPage";
import Step10SignaturePage from "./OnboardingSteps/Step10SignaturePage";
import {
  sortWorkHistoriesByDate,
  validateWorkHistoryCoverage,
} from "../../utils/workHistoryCoverage";
import { toDateInputValue } from "../../utils/formatDateOnly";
import "../../styles/Form.css";

const FULL_STEPS_CATEGORIES = [2, 8, 11];

const initialForm = {
  address1: "",
  address2: "",
  city: "",
  province: "",
  country: "",
  postal_code: "",
  dob: "",
  refer_status: "No",
  refer_by_name: "",
  refer_by_id: "",
  dq_legal_right_work_us: "",
  dq_legal_right_enter_canada: "",
  us_doc_type: "",
  dq_previous_nwt_employee: "",
  dq_previous_nwt_from: "",
  dq_previous_nwt_to: "",
  dl_license_no: "",
  dl_class: [],
  dl_condition: [],
  dl_province: "",
  dl_country: "",
  dl_expdate: "",
  dl_driver_status: "active",
  additionalLicenses: [],
  additionalLicensesHasOther: "No",
  pendingAdditionalLicense: {
    dl_license_no: "",
    dl_class: "",
    dl_condition: "",
    dl_province: "",
    dl_country: "Canada",
    dl_expdate: "",
    dl_driver_status: "inactive",
  },
  dq_conviction: "",
  dq_conviction_date: "",
  dq_fast_card: "",
  dq_fast_card_no: "",
  dq_fast_card_expiry: "",
  dq_fast_card_revoked: "",
  dq_fast_card_revoked_date: "",
  dq_fast_card_revoked_detail: "",
  dq_accidents_last5yr: "",
  dq_accidents_last5yr_date: "",
  dq_accidents_last5yr_detail: "",
  dq_violations_last3yr: "",
  dq_violations_last3yr_date: "",
  dq_violations_last3yr_detail: "",
  dq_physical_condition: "",
  dq_physical_condition_detail: "",
  dq_lift_40lb: "",
  dq_training: "",
  dq_trainings: [],
  dq_safe_driving_awards: "",
  dq_awards: [],
  dq_commercial_license_date: "",
  dq_license_denied: "",
  dq_denied_explanation: "",
  dq_abstract_clean: "",
  dq_abstract_explanation: "",
  dq_license_suspended: "",
  dq_suspended_explanation: "",
  dq_education: "",
  dq_school: "",
  dq_experience: [],
  dq_reg_travelled: [],
  dq_equip_operated: [],
  dq_exp_detail: {},
  dq_equip_operated_detail: {},
  user_signature: "",
  workHistories: [],
  workHistoryDraft: null,
  workHistoryStillWorking: false,
  editingWorkHistory: null,
  editingIndex: null,
};

const getValue = (result, fallback) =>
  result.status === "fulfilled" ? result.value.data : fallback;

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];

  if (typeof value === "string") {
    try {
      return toArray(JSON.parse(value));
    } catch {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return typeof value === "object" ? [] : [value];
};

const toObject = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return toObject(JSON.parse(value));
    } catch {
      return {};
    }
  }

  return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const CandidateOnboardingPage = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const BASE = process.env.REACT_APP_API_BASE_URL || "";
  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token],
  );

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(true);
  const [countries, setCountries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [dbWorkHistories, setDbWorkHistories] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingWorkHistory, setEditingWorkHistory] = useState(null);
  const [userId, setUserId] = useState(null);
  const [job, setJob] = useState(null);
  const draftTimeoutRef = useRef(null);
  const hydrateRequestIdRef = useRef(0);
  const submittingRef = useRef(false);

  const category = Number(job?.p_category);
  const allowedSteps = FULL_STEPS_CATEGORIES.includes(category)
    ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    : [1, 2, 4, 7, 8, 9, 10];

  const hydrateOnboarding = useCallback(async () => {
    if (!token || !applicationId) {
      setHydrating(false);
      return;
    }

    const requestId = ++hydrateRequestIdRef.current;
    setHydrating(true);

    try {
      const profileRes = await axios.get(`${BASE}/api/candidate/user/profile`, {
        headers: authHeader,
      });

      if (hydrateRequestIdRef.current !== requestId) return;

      const profileData = profileRes.data || {};
      setUserId(profileData.id || null);

      const results = await Promise.allSettled([
        axios.get(`${BASE}/api/candidate/application/${applicationId}/job-id`, {
          headers: authHeader,
        }),
        axios.get(`${BASE}/api/candidate/user/license/${profileData.id}`, {
          headers: authHeader,
        }),
        axios.get(`${BASE}/api/candidate/user/qualifications/${profileData.id}`, {
          headers: authHeader,
        }),
        axios.get(`${BASE}/api/candidate/user/work-history/${profileData.id}`, {
          headers: authHeader,
        }),
        axios.get(`${BASE}/api/candidate/user/onboarding-draft/${applicationId}`, {
          headers: authHeader,
        }),
      ]);

      if (hydrateRequestIdRef.current !== requestId) return;

      const [jobIdRes, licenseRes, qualificationsRes, workHistoryRes, draftRes] =
        results;

      const jobId = getValue(jobIdRes, {}).job_id;
      const primaryLicense = getValue(licenseRes, {}) || {};
      const qualifications = getValue(qualificationsRes, {}) || {};
      const savedWorkHistory = getValue(workHistoryRes, []) || [];
      const draftPayload = getValue(draftRes, {});
      const draft = draftPayload?.draft || {};

      if (jobId) {
        try {
          const jobRes = await axios.get(`${BASE}/api/open-positions/${jobId}`);
          if (hydrateRequestIdRef.current === requestId) {
            setJob(jobRes.data || null);
          }
        } catch (err) {
          console.error("Failed to load onboarding job:", err);
        }
      } else {
        setJob(null);
      }

      setDbWorkHistories(sortWorkHistoriesByDate(savedWorkHistory));
      setStep(Number(draftPayload?.currentStep) || 1);
      const hydratedForm = {
        ...initialForm,
        ...profileData,
        refer_status: profileData.refer_status || "No",
        refer_by_name: profileData.refer_by_name || "",
        refer_by_id: profileData.refer_by_id || "",
        ...primaryLicense,
        ...qualifications,
        ...draft,
        additionalLicenses: draft.additionalLicenses || [],
        additionalLicensesHasOther:
          draft.additionalLicensesHasOther ||
          ((draft.additionalLicenses || []).length > 0 ? "Yes" : "No"),
        pendingAdditionalLicense: {
          ...initialForm.pendingAdditionalLicense,
          ...(draft.pendingAdditionalLicense || {}),
        },
        workHistories: sortWorkHistoriesByDate(draft.workHistories || []),
        workHistoryDraft: draft.workHistoryDraft || null,
        workHistoryStillWorking: Boolean(draft.workHistoryStillWorking),
        editingWorkHistory: null,
        editingIndex: null,
        dob:
          toDateInputValue(draft.dob) ||
          toDateInputValue(profileData.dob),
      };

      setForm({
        ...hydratedForm,
        dl_class: toArray(hydratedForm.dl_class),
        dl_condition: toArray(hydratedForm.dl_condition),
        dq_trainings: toArray(hydratedForm.dq_trainings),
        dq_awards: toArray(hydratedForm.dq_awards),
        dq_experience: toArray(hydratedForm.dq_experience),
        dq_reg_travelled: toArray(hydratedForm.dq_reg_travelled),
        dq_equip_operated: toArray(hydratedForm.dq_equip_operated),
        dq_exp_detail: toObject(hydratedForm.dq_exp_detail),
        dq_equip_operated_detail: toObject(
          hydratedForm.dq_equip_operated_detail,
        ),
      });

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          console.error(`Failed to hydrate onboarding resource ${index + 1}:`, result.reason);
        }
      });
    } catch (err) {
      if (hydrateRequestIdRef.current !== requestId) return;
      console.error("Failed to load onboarding data:", err);
    } finally {
      if (hydrateRequestIdRef.current === requestId) {
        setHydrating(false);
      }
    }
  }, [BASE, applicationId, authHeader, token]);

  useEffect(() => {
    hydrateOnboarding();
  }, [hydrateOnboarding]);

  useEffect(() => {
    window.addEventListener("pageshow", hydrateOnboarding);
    window.addEventListener("profileUpdated", hydrateOnboarding);

    return () => {
      window.removeEventListener("pageshow", hydrateOnboarding);
      window.removeEventListener("profileUpdated", hydrateOnboarding);
    };
  }, [hydrateOnboarding]);

  useEffect(() => {
    async function fetchCountries() {
      try {
        const { data } = await axios.get(`${BASE}/api/dropdowns/countries`);
        const list = Array.isArray(data) ? data : data?.countries || [];
        setCountries(list);
      } catch (err) {
        console.error("Failed to load countries:", err);
        setCountries([]);
      }
    }

    fetchCountries();
  }, [BASE]);

  useEffect(() => {
    if (form.dl_country !== "Canada" || !form.dl_province) {
      setClasses([]);
      setConditions([]);
      return;
    }

    const fetchLicenseMeta = async () => {
      try {
        const res = await axios.get(`${BASE}/api/dropdowns/province-by-name`, {
          params: {
            country: form.dl_country,
            province: form.dl_province,
          },
          headers: authHeader,
        });

        const provinceId = res.data?.id;
        if (!provinceId) return;

        const [classesRes, conditionsRes] = await Promise.all([
          axios.get(`${BASE}/api/dropdowns/license-classes/${provinceId}`, {
            headers: authHeader,
          }),
          axios.get(`${BASE}/api/dropdowns/license-conditions/${provinceId}`, {
            headers: authHeader,
          }),
        ]);

        setClasses(classesRes.data?.classes || []);
        setConditions(conditionsRes.data?.conditions || []);
      } catch (err) {
        console.error("Failed to load license rules", err);
        setClasses([]);
        setConditions([]);
      }
    };

    fetchLicenseMeta();
  }, [BASE, authHeader, form.dl_country, form.dl_province]);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  useEffect(() => {
    if (!userId || hydrating) return;

    const draftPayload = {
      currentStep: step,
      draft: {
        ...form,
        editingWorkHistory: null,
        editingIndex: null,
      },
    };

    draftTimeoutRef.current = setTimeout(() => {
      axios
        .put(
          `${BASE}/api/candidate/user/onboarding-draft/${applicationId}`,
          draftPayload,
          { headers: authHeader },
        )
        .catch((err) => console.error("Failed to save onboarding draft:", err));
    }, 700);

    return () => {
      if (draftTimeoutRef.current) {
        clearTimeout(draftTimeoutRef.current);
      }
    };
  }, [BASE, applicationId, authHeader, form, hydrating, step, userId]);

  const nextStep = () => {
    const index = allowedSteps.indexOf(step);
    if (index < allowedSteps.length - 1) {
      setStep(allowedSteps[index + 1]);
    }
  };

  const prevStep = () => {
    const index = allowedSteps.indexOf(step);
    if (index > 0) {
      setStep(allowedSteps[index - 1]);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this application?")) {
      return;
    }

    try {
      await axios.delete(`${BASE}/api/candidate/apply/cancel/${applicationId}`, {
        headers: authHeader,
      });
      alert("Application cancelled");
      navigate("/");
    } catch (err) {
      console.error(err);
      alert("Failed to cancel application");
    }
  };

  const clean = (v) => (v === "" ? null : v);

  const handleComplete = async (signatureOverride = null) => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      setLoading(true);
      const userSignature = signatureOverride || form.user_signature || null;

      if (!userSignature) {
        alert("Please add your signature before submitting.");
        return;
      }

      const coverage = validateWorkHistoryCoverage([
        ...dbWorkHistories,
        ...(form.workHistories || []),
      ]);
      if (!coverage.isValid) {
        alert(coverage.message);
        setStep(9);
        return;
      }

      const payload = {
        ...form,
        user_signature: userSignature,
        refer_by:
          form.refer_status === "Yes"
            ? JSON.stringify({
                name: form.refer_by_name || "",
                employeeId: form.refer_by_id || "",
              })
            : null,
      };

      await axios.put(
        `${BASE}/api/candidate/user/onboarding/${applicationId}`,
        payload,
        { headers: authHeader },
      );

      await axios.post(
        `${BASE}/api/candidate/user/license/${userId}`,
        {
          dl_license_no: form.dl_license_no,
          dl_class: form.dl_class,
          dl_condition: form.dl_condition,
          dl_province: form.dl_province,
          dl_country: form.dl_country,
          dl_expdate: form.dl_expdate,
        },
        { headers: authHeader },
      );

      for (const lic of form.additionalLicenses || []) {
        await axios.post(`${BASE}/api/candidate/user/license/add/${userId}`, lic, {
          headers: authHeader,
        });
      }

      await axios.post(
        `${BASE}/api/candidate/user/qualifications/${userId}`,
        {
          dq_conviction: clean(form.dq_conviction),
          dq_conviction_date: clean(form.dq_conviction_date),
          dq_fast_card: clean(form.dq_fast_card),
          dq_fast_card_no: clean(form.dq_fast_card_no),
          dq_fast_card_expiry: clean(form.dq_fast_card_expiry),
          dq_fast_card_revoked: clean(form.dq_fast_card_revoked),
          dq_fast_card_revoked_date: clean(form.dq_fast_card_revoked_date),
          dq_fast_card_revoked_detail: clean(form.dq_fast_card_revoked_detail),
          dq_accidents_last5yr: clean(form.dq_accidents_last5yr),
          dq_accidents_last5yr_date: clean(form.dq_accidents_last5yr_date),
          dq_accidents_last5yr_detail: clean(form.dq_accidents_last5yr_detail),
          dq_violations_last3yr: clean(form.dq_violations_last3yr),
          dq_violations_last3yr_date: clean(form.dq_violations_last3yr_date),
          dq_violations_last3yr_detail: clean(form.dq_violations_last3yr_detail),
          dq_physical_condition: clean(form.dq_physical_condition),
          dq_physical_condition_detail: clean(form.dq_physical_condition_detail),
          dq_lift_40lb: clean(form.dq_lift_40lb),
          dq_training: clean(form.dq_training),
          dq_trainings: form.dq_trainings || [],
          dq_safe_driving_awards: clean(form.dq_safe_driving_awards),
          dq_awards: form.dq_awards || [],
          dq_commercial_license_date: clean(form.dq_commercial_license_date),
          dq_license_denied: clean(form.dq_license_denied),
          dq_denied_explanation: clean(form.dq_denied_explanation),
          dq_abstract_clean: clean(form.dq_abstract_clean),
          dq_abstract_explanation: clean(form.dq_abstract_explanation),
          dq_license_suspended: clean(form.dq_license_suspended),
          dq_suspended_explanation: clean(form.dq_suspended_explanation),
          dq_education: clean(form.dq_education),
          dq_school: clean(form.dq_school),
          dq_experience: form.dq_experience,
          dq_reg_travelled: form.dq_reg_travelled,
          dq_equip_operated: form.dq_equip_operated,
          dq_exp_detail: form.dq_exp_detail,
          dq_equip_operated_detail: form.dq_equip_operated_detail,
        },
        { headers: authHeader },
      );

      for (const wh of form.workHistories || []) {
        await axios.post(`${BASE}/api/candidate/user/work-history/${userId}`, wh, {
          headers: authHeader,
        });
      }

      await axios.post(
        `${BASE}/api/candidate/user/onboarding/complete/${applicationId}`,
        {},
        { headers: authHeader },
      );

      alert("Onboarding completed");
      window.location.href = "https://hr.newwaytrans.com/";
    } catch (err) {
      console.error(err);
      alert("Failed to save onboarding info.");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const index = allowedSteps.indexOf(step);
  const progress = Math.round(((index + 1) / allowedSteps.length) * 100);

  if (hydrating) {
    return <div className="container my-4">Loading your saved application...</div>;
  }

  return (
    <>
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
          style={{ zIndex: 2000 }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              style={{ width: "4rem", height: "4rem" }}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 fw-bold">Submitting your application...</p>
          </div>
        </div>
      )}

      <div className="container my-4 candidate-application">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Candidate Application</h2>
          <button type="button" className="btn btn-danger" onClick={handleCancel}>
            Cancel
          </button>
        </div>

        <div className="progress mb-4">
          <div className="progress-bar" style={{ width: `${progress}%` }}>
            {progress}%
          </div>
        </div>

        {step === 1 && (
          <Step1Address
            form={form}
            setForm={setForm}
            countries={countries}
            handleChange={handleChange}
            nextStep={nextStep}
          />
        )}

        {step === 2 && (
          <Step2Personal
            form={form}
            job={job}
            handleChange={handleChange}
            prevStep={prevStep}
            nextStep={nextStep}
          />
        )}

        {step === 3 && (
          <Step3License
            form={form}
            countries={countries}
            classes={classes}
            conditions={conditions}
            handleChange={handleChange}
            prevStep={prevStep}
            nextStep={nextStep}
          />
        )}

        {step === 4 && (
          <Step4Qualifications
            form={form}
            job={job}
            handleChange={handleChange}
            prevStep={prevStep}
            nextStep={nextStep}
            setForm={setForm}
          />
        )}

        {step === 5 && userId && (
          <Step5AdditionalLicenses
            userId={userId}
            authHeader={authHeader}
            prevStep={prevStep}
            nextStep={nextStep}
            countries={countries}
            form={form}
            setForm={setForm}
            primaryLicense={form}
          />
        )}

        {step === 6 && (
          <Step6DriverQuestions
            form={form}
            handleChange={handleChange}
            prevStep={prevStep}
            nextStep={nextStep}
          />
        )}

        {step === 7 && (
          <Step7FinalQuestions
            form={form}
            job={job}
            setForm={setForm}
            prevStep={prevStep}
            nextStep={
              dbWorkHistories.length > 0 || (form.workHistories || []).length > 0
                ? () => setStep(9)
                : nextStep
            }
          />
        )}

        {step === 8 && (
          <Step8WorkHistoryPage
            form={form}
            setForm={setForm}
            dbWorkHistories={dbWorkHistories}
            prevStep={() => setStep(7)}
            nextStep={nextStep}
            editingWorkHistory={editingWorkHistory}
            editingIndex={editingIndex}
            setEditingWorkHistory={setEditingWorkHistory}
            setEditingIndex={setEditingIndex}
          />
        )}

        {step === 9 && (
          <Step9ReviewWorkHistoryPage
            form={form}
            setForm={setForm}
            dbWorkHistories={dbWorkHistories}
            prevStep={() => setStep(7)}
            setStep={setStep}
            setEditingWorkHistory={setEditingWorkHistory}
            setEditingIndex={setEditingIndex}
            handleComplete={handleComplete}
          />
        )}

        {step === 10 && (
          <Step10SignaturePage
            form={form}
            setForm={setForm}
            prevStep={prevStep}
            handleComplete={handleComplete}
          />
        )}
      </div>
    </>
  );
};

export default CandidateOnboardingPage;
